import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {EAccountConfirmationStatus} from '@app/auth-config/models/auth-state.model';
import {IChangePasswordForm} from '@app/auth-config/models/change-password-form.model';
import {IProfileInfoForm} from '@app/auth-config/models/profile.form.model';
import {changePassword, resetChangePasswordStatus} from '@app/auth-config/state/auth.actions';
import {selectChangePasswordSuccessMessageState} from '@app/auth-config/state/auth.selector';
import {StringUtils} from '@app/core/utils/string.utils';
import {ToastMessageUtils} from '@app/core/utils/toast-message.utils';
import {OneLower_OneUpper_OneDigit_OneSpecial_BetweenRangeChar_Regex, OnlyDigits_Regex} from '@app/shared/constants';
import {ECustomerUpdateStatus} from '@app/shared/enums/header.enum';
import {EEmailNotifications} from '@app/shared/enums/profile.enum';
import {EToastMessageOptions, EToastMessageSeverity} from '@app/shared/enums/toast-message.enum';
import {ICountry} from '@app/shared/models/country.model';
import {ICustomerInfo, ICustomerOrganization, ICustomerProfileState} from '@app/shared/models/header.model';
import {resetUpdateCustomerProfileStatus, updateCustomerProfile} from '@app/shared/state/header/header.action';
import {selectCustomerProfileState, selectUpdateCustomerStatus} from '@app/shared/state/header/header.selector';
import {HeaderUtils} from '@app/shared/utils/header.utils';
import {loadCountries} from '@app/signUp/state/sign-up.actions';
import {selectCountries} from '@app/signUp/state/sign-up.selector';
import {Store} from '@ngrx/store';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import {MessageService, ToastMessageOptions} from 'primeng/api';
import {Observable, Subject, map, takeUntil, tap} from 'rxjs';
import { Bind } from 'primeng/bind';
import { Select } from 'primeng/select';
import { InputText } from 'primeng/inputtext';
import { Checkbox } from 'primeng/checkbox';
import { Button } from 'primeng/button';
import { CustomPasswordComponent } from '../../../shared/components/custom-password/custom-password.component';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'app-profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.scss'],
    imports: [
        FormsModule,
        ReactiveFormsModule,
        Bind,
        Select,
        InputText,
        Checkbox,
        Button,
        CustomPasswordComponent,
        AsyncPipe,
        TranslatePipe,
    ],
})
export class ProfileComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly formBuilder = inject(FormBuilder);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);

  emailNotifications = [
    {checked: true, name: EEmailNotifications.NewOrder},
    {checked: true, name: EEmailNotifications.ResultsReady},
  ];
  countries: ICountry[] | null = null;
  countries$: Observable<ICountry[]> | null = null;
  customerProfile: ICustomerProfileState | null = null;
  customerOrganization: ICustomerOrganization | null = null;
  customerAddress = '';
  profileInfoForm: FormGroup<IProfileInfoForm>;
  changePasswordForm: FormGroup<IChangePasswordForm>;
  customerProfileState$: Observable<{
    customerProfile: ICustomerProfileState | null;
    customerOrganization: ICustomerOrganization | null;
    customerOrganizationAddress: string;
  }>;
  errorMessage$: Observable<ToastMessageOptions[]> | null = null;
  globalToast = 'global-toast';
  profileSaveInProgress = false;
  passwordSaveInProgress = false;
  private readonly unsubscribe$ = new Subject<void>();

  constructor() {
    this.profileInfoForm = this.formBuilder.group({
      phoneNumber: ['', [Validators.required, Validators.pattern(OnlyDigits_Regex)]],
      prefixPhoneNumber: ['', Validators.required],
      emailNotification: [[true, true]],
    });

    this.changePasswordForm = this.formBuilder.group(
      {
        oldPassword: [
          '',
          [
            Validators.required,
            Validators.pattern(RegExp(OneLower_OneUpper_OneDigit_OneSpecial_BetweenRangeChar_Regex)),
          ],
        ],
        newPassword: [
          '',
          [
            Validators.required,
            Validators.pattern(RegExp(OneLower_OneUpper_OneDigit_OneSpecial_BetweenRangeChar_Regex)),
          ],
        ],
      },
      {validators: [this.changePasswordMatchValidator]}
    );

    this.customerProfileState$ = this.store.select(selectCustomerProfileState);

    this.countries$ = this.store.select(selectCountries).pipe(
      map(countries => {
        const flattenCountries = [...(countries ?? [])].sort((a, b) =>
          a.twoLetterIsoCode.localeCompare(b.twoLetterIsoCode)
        );
        this.countries =
          flattenCountries.map(country => ({
            ...country,
            phoneNumberPrefixWithPlus: `(${country.twoLetterIsoCode}) +${country.phoneNumberPrefix}`,
          })) ?? [];

        return this.countries;
      })
    );

    this.store
      .select(selectUpdateCustomerStatus)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((status: ECustomerUpdateStatus) => {
          if (status === ECustomerUpdateStatus.Success || status === ECustomerUpdateStatus.Failure) {
            this.profileSaveInProgress = false;
          }
        })
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.dispatchLoadCountries();
    this.initCustomerProfileSubscription();
    this.initChangePasswordSuccessSubscription();
  }

  getCountryName(countryId: number | null | undefined) {
    return this.countries?.find(x => x.id === countryId)?.name ?? '';
  }

  onProfileFormSubmit(
    customerProfileState: {
      customerProfile: ICustomerProfileState | null;
      customerOrganization: ICustomerOrganization | null;
      customerOrganizationAddress: string;
    },
    countries: ICountry[]
  ): void {
    if (!HeaderUtils.validateCustomerProfileProperties(customerProfileState)) {
      this.store.dispatch(resetUpdateCustomerProfileStatus());
      this.profileSaveInProgress = true;
      this.store.dispatch(
        updateCustomerProfile({
          customerProfile: this.getUpdateCustomerProfilePayload(customerProfileState, countries),
        })
      );
    } else {
      ToastMessageUtils.showMessage({
        messageService: this.messageService,
        toastKey: this.globalToast,
        severity: EToastMessageSeverity.Error,
        messageDetail: this.translate.instant('Profile.UpdateCustomerError'),
        closable: true,
        life: EToastMessageOptions.LifeTime,
      });
    }
  }

  onChangePasswordFormSubmit(): void {
    const oldPassword = this.changePasswordForm.value.oldPassword;
    const newPassword = this.changePasswordForm.value.newPassword;

    if (oldPassword && newPassword) {
      this.store.dispatch(resetChangePasswordStatus());
      this.passwordSaveInProgress = true;
      this.store.dispatch(
        changePassword({
          oldPassword: oldPassword,
          newPassword: newPassword,
        })
      );
    }
  }

  private dispatchLoadCountries(): void {
    this.store.dispatch(loadCountries());
  }

  private getUpdateCustomerProfilePayload(
    customerProfileState: {
      customerProfile: ICustomerProfileState | null;
      customerOrganization: ICustomerOrganization | null;
      customerOrganizationAddress: string;
    },
    countries: ICountry[]
  ): ICustomerInfo {
    return {
      id: customerProfileState?.customerProfile?.id,
      firstName: customerProfileState?.customerProfile?.firstName,
      lastName: customerProfileState?.customerProfile?.lastName,
      email: customerProfileState?.customerProfile?.aspNetUsers?.email,
      medicalSpecialtyId: customerProfileState?.customerProfile?.medicalSpecialty?.id,
      doctorId: customerProfileState?.customerProfile?.doctorId,
      phoneNumber: this.profileInfoForm.value.phoneNumber,
      prefixPhoneNumber: this.getPhoneNumberPrefixWithPlus(countries),
      roleId: customerProfileState?.customerProfile?.roleId,
      organizationId: customerProfileState?.customerOrganization?.organizationId,
    };
  }

  private getPhoneNumberPrefixWithPlus(countries: ICountry[]): string | null {
    const countryPhoneNumberPrefixWithPlus = this.getPhoneNumberPrefix(countries);

    if (countryPhoneNumberPrefixWithPlus) {
      return countryPhoneNumberPrefixWithPlus.split(' ')[1];
    }
    return null;
  }

  private getPhoneNumberPrefix(countries: ICountry[]): string | null {
    return (
      countries.find(country => country.phoneNumberPrefix === this.profileInfoForm.value.prefixPhoneNumber)
        ?.phoneNumberPrefixWithPlus ?? null
    );
  }

  private changePasswordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const oldPassword = control?.get('oldPassword')?.value;
    const newPassword = control?.get('newPassword')?.value;

    const isOldAndNewPasswordMatched = oldPassword && newPassword && oldPassword === newPassword;

    if (isOldAndNewPasswordMatched) {
      control?.get('newPassword')?.setErrors({passwordMatch: true});
    } else {
      control?.get('newPassword')?.setErrors(null);
    }
    return null;
  }

  private initCustomerProfileSubscription(): void {
    this.customerProfileState$
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(value => {
          if (value.customerProfile?.phoneNumber && value.customerProfile?.prefixPhoneNumber) {
            this.profileInfoForm.patchValue({
              phoneNumber: value.customerProfile?.phoneNumber,
              prefixPhoneNumber: StringUtils.replaceSpecialCharacterPlusAndSpace(
                value.customerProfile?.prefixPhoneNumber
              ),
            });
          }
        })
      )
      .subscribe();
  }

  private initChangePasswordSuccessSubscription(): void {
    this.store
      .select(selectChangePasswordSuccessMessageState)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((status: EAccountConfirmationStatus) => {
          this.changePasswordForm.reset();
          if (status === EAccountConfirmationStatus.Changed || status === EAccountConfirmationStatus.Error) {
            this.passwordSaveInProgress = false;
          }
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
